import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Radar, GitCompareArrows, FileSearch, Quote, CheckCircle2 } from "lucide-react";

const VP = [
  { t: "Regulatory Intelligence senza rumore", d: "Ricevi solo le novità rilevanti per i tuoi siti e i tuoi prodotti." },
  { t: "AI con fonti verificabili", d: "Ogni risposta è collegata a evidenze regolatorie citabili." },
  { t: "Multi-client. Multi-market.", d: "Valuta simultaneamente l'impatto su clienti e mercati diversi." },
  { t: "Dal requisito all'azione", d: "Trasforma una novità in assessment, task ed evidenza." },
  { t: "Inspection ready", d: "Integra intelligence regolatoria e preparazione alle ispezioni." },
];

const FLOW = ["Signal", "Applicability", "Impact", "Decision", "Action", "Evidence", "Closure"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="font-head font-bold text-lg tracking-tight">RegIntel <span className="text-blue-600">CDMO</span></div>
          <Link to="/login" data-testid="nav-login-btn"
            className="text-sm font-medium px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors">
            Accedi alla piattaforma
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="ri-fade-in">
            <div className="ri-overline mb-4">Regulatory Intelligence · Compliance · Action</div>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Dalla novità regolatoria all'azione.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Una piattaforma di Regulatory Intelligence e Compliance progettata per CDMO farmaceutiche.
              Monitora i cambiamenti regolatori, valuta l'impatto su siti, prodotti e clienti, interroga
              le fonti con l'AI e trasforma ogni requisito in un'azione tracciabile.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 transition-colors">
                Accedi alla piattaforma <ArrowRight size={18} />
              </Link>
              <a href="#come-funziona" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 font-medium hover:bg-slate-50 transition-colors">
                Scopri come funziona
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              {["FDA", "EMA", "PMDA", "NMPA", "ICH", "PIC/S", "WHO"].map((a) => (
                <span key={a} className="font-mono">{a}</span>
              ))}
            </div>
          </div>
          <div className="ri-fade-in" style={{ animationDelay: "0.1s" }}>
            <div className="rounded-2xl border border-slate-200 shadow-xl overflow-hidden bg-slate-900">
              <img src="https://images.unsplash.com/photo-1668600418844-5b3d2e381e10?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Cleanroom sterile manufacturing" className="w-full h-72 object-cover opacity-90" />
              <div className="p-5 text-slate-300">
                <div className="ri-overline" style={{ color: "#64748B" }}>Workflow digitale tracciabile</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  {FLOW.map((s, i) => (
                    <React.Fragment key={s}>
                      <span className="text-xs font-mono px-2 py-1 rounded bg-slate-800 text-blue-300">{s}</span>
                      {i < FLOW.length - 1 && <ArrowRight size={12} className="text-slate-600" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KNOW / UNDERSTAND / ... */}
      <section id="come-funziona" className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-5 gap-4">
          {[["KNOW", "cosa cambia", Radar], ["UNDERSTAND", "perché conta", FileSearch],
            ["ASSESS", "cosa impatta", GitCompareArrows], ["ACT", "cosa fare", CheckCircle2],
            ["PROVE", "dimostrare che è fatto", ShieldCheck]].map(([k, d, Icon], i) => (
            <div key={k} className="rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow ri-fade-in"
              style={{ animationDelay: `${i * 0.06}s` }}>
              <Icon size={22} className="text-blue-600 mb-3" />
              <div className="font-head font-bold text-lg">{k}</div>
              <div className="text-sm text-slate-500">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Value props */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-head text-2xl sm:text-3xl font-semibold tracking-tight mb-10">
            Regulatory Intelligence progettata per CDMO farmaceutiche.
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {VP.map((v) => (
              <div key={v.t} className="rounded-xl bg-white border border-slate-200 p-6">
                <CheckCircle2 size={18} className="text-green-600 mb-3" />
                <div className="font-semibold text-slate-900">{v.t}</div>
                <div className="text-sm text-slate-500 mt-1">{v.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer + CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="rounded-2xl bg-slate-900 text-white p-10 text-center">
          <h2 className="font-head text-2xl sm:text-3xl font-semibold">Un Regulatory Operating System per il mondo CDMO.</h2>
          <Link to="/login"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 font-medium transition-colors">
            Accedi alla piattaforma <ArrowRight size={18} />
          </Link>
        </div>
        <p className="mt-8 text-xs text-slate-400 max-w-3xl mx-auto text-center flex gap-2 justify-center">
          <Quote size={14} className="flex-shrink-0 mt-0.5" />
          La piattaforma supporta l'analisi regolatoria e il processo decisionale professionale. Le valutazioni
          generate dall'intelligenza artificiale devono essere verificate da personale qualificato prima di essere
          utilizzate per decisioni regolatorie, GMP o qualità.
        </p>
      </section>
    </div>
  );
}
